/**
 * Production verification UI kit.
 *
 * One module for every surface primitive the product pages share. Built on
 * `@excellent/ui` atoms (Button, Card, Alert, SearchInput, Select, DataTable,
 * EmptyState, SectionTitle) so pages do not hand-roll `vp-*` markup or fork a
 * second library. Prototype `prototypes/verification/ui.tsx` stays archive-only.
 *
 * Verdict and Severity stay product-specific: UNKNOWN must not read as FAIL,
 * and colour alone cannot carry that (prototype spec). Everything else is a
 * composition of the shared atoms.
 */

import { useEffect, useRef, useState, type ComponentProps, type MouseEvent, type ReactNode } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  DataTable as UiDataTable,
  EmptyState,
  Input,
  Label,
  SearchInput,
  SectionTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  formatRelativeTime,
  type DataTableColumn,
  type DataTableProps,
} from "@excellent/ui";
import { displayLabel } from "./display-label";
import { Icon } from "./icons";

export type Tone = "pass" | "fail" | "unknown" | "waived" | "running" | "accent" | "neutral";
type VerificationStatus = "PASS" | "MISMATCH" | "FAIL" | "UNKNOWN" | "NOT_RUN" | "PENDING";

// Exhaustive `Record`s, not partial maps with a `??` fallback: a status absent
// from either one renders as a blank glyph in an untoned badge, which reads to a
// customer as "nothing to report". MISMATCH shares FAIL's tone because both are
// red states; the LABEL, not the colour, distinguishes an accusation from a
// refusal.
//
// PENDING gets its OWN tone rather than borrowing one, because every neighbour
// would misreport it: `unknown` is the colour of a problem a human must fix,
// `neutral` is NOT_RUN's "nothing here", and `pass`/`fail` are verdicts it has
// not reached. `running` is the existing tone for work still in flight, which is
// exactly what a verdict inside its window is.
const STATUS_TONE: Record<VerificationStatus, Tone> = {
  PASS: "pass",
  MISMATCH: "fail",
  FAIL: "fail",
  UNKNOWN: "unknown",
  NOT_RUN: "neutral",
  PENDING: "running",
};

const STATUS_GLYPH: Record<VerificationStatus, string> = {
  PASS: "✓",
  MISMATCH: "≠",
  FAIL: "✕",
  UNKNOWN: "?",
  NOT_RUN: "–",
  // An hourglass, distinct from UNKNOWN's "?" and NOT_RUN's dash: the reader is
  // being told to come back, not that something is missing or broken.
  PENDING: "⧖",
};

export { displayLabel } from "./display-label";

export {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  EmptyState,
  Input,
  Label,
  SearchInput,
  SectionTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
};
export type { DataTableColumn };

const BUTTON_VARIANT = {
  primary: "default",
  outline: "outline",
  ghost: "ghost",
  accept: "accept",
  reject: "reject",
} as const;

export type ActionButtonVariant = keyof typeof BUTTON_VARIANT;

export function ActionButton({
  actionId,
  children,
  onClick,
  disabled,
  reason,
  variant = "outline",
  type = "button",
  size,
  ...rest
}: {
  actionId: string;
  children: ReactNode;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  reason?: string;
  variant?: ActionButtonVariant;
  type?: "button" | "submit";
  size?: ComponentProps<typeof Button>["size"];
} & Omit<ComponentProps<"button">, "type" | "onClick" | "disabled" | "children">) {
  return (
    <Button
      type={type}
      variant={BUTTON_VARIANT[variant]}
      size={size}
      disabled={disabled}
      title={disabled ? reason : undefined}
      onClick={disabled ? undefined : onClick}
      data-action-id={actionId}
      {...rest}
    >
      {children}
    </Button>
  );
}

export type ActionMenuItem = {
  label: string;
  actionId: string;
  onClick?: () => void;
  disabled?: boolean;
  reason?: string;
};

export function ActionMenu({
  actionId,
  items,
}: {
  actionId: string;
  items: ActionMenuItem[];
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: Event) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <ActionButton
        actionId={actionId}
        variant="outline"
        size="icon"
        aria-label="More actions"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        <Icon name="menu" size={16} />
      </ActionButton>
      <div
        role="menu"
        className={
          open
            ? "absolute right-0 z-50 mt-1 flex min-w-[12rem] flex-col gap-0.5 rounded-md border border-border bg-background p-1"
            : "sr-only"
        }
      >
        {items.map((item) => (
          <ActionButton
            key={item.actionId}
            actionId={item.actionId}
            variant="ghost"
            disabled={item.disabled}
            reason={item.reason}
            className="w-full justify-start"
            onClick={() => {
              item.onClick?.();
              setOpen(false);
            }}
          >
            {item.label}
          </ActionButton>
        ))}
      </div>
    </div>
  );
}

export function PageActions({
  primary,
  secondary,
  menu,
}: {
  primary?: ReactNode;
  secondary?: ReactNode;
  menu?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {menu}
      {secondary}
      {primary}
    </div>
  );
}

export function ButtonRow({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2" {...(onClick ? { onClick } : {})}>
      {children}
    </div>
  );
}

export function ScopeTabs<K extends string>({
  value,
  onChange,
  options,
  actionId,
}: {
  value: K;
  onChange: (key: K) => void;
  options: { key: K; label: string; actionId: string }[];
  actionId?: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1" role="group" data-action-id={actionId}>
      {options.map((option) => (
        <ActionButton
          key={option.key}
          actionId={option.actionId}
          variant={option.key === value ? "primary" : "outline"}
          aria-pressed={option.key === value}
          onClick={() => onChange(option.key)}
        >
          {option.label}
        </ActionButton>
      ))}
    </div>
  );
}

export function Verdict({
  status,
  size,
}: {
  status: string;
  size?: "lg";
}) {
  const key = status as VerificationStatus;
  const tone: Tone = STATUS_TONE[key] ?? "neutral";
  const glyph = STATUS_GLYPH[key] ?? "–";
  return (
    <span className="vp-badge" data-tone={tone} data-size={size} data-slot="verdict">
      <span className="vp-badge-glyph" aria-hidden="true">
        {glyph}
      </span>
      {displayLabel(status)}
    </span>
  );
}

export function Severity({ level }: { level: string }) {
  return (
    <span className="vp-sev" data-sev={level} data-slot="severity">
      <span className="vp-sev-dot" aria-hidden="true" />
      {displayLabel(level)}
    </span>
  );
}

export type AssuranceSupportValue = "yes" | "no" | "qualified" | "unknown";

const ASSURANCE_SUPPORT_TONE: Record<AssuranceSupportValue, Tone> = {
  yes: "pass",
  no: "fail",
  qualified: "waived",
  unknown: "unknown",
};

// Same discipline as STATUS_GLYPH above: a support answer is never carried by
// colour alone. `unknown` gets the same shape/tone family as UNKNOWN verdicts
// so it never reads as a pass, and `qualified` (holds only under a condition
// the reader must check) gets its own distinct shape rather than borrowing
// either yes's or unknown's.
const ASSURANCE_SUPPORT_GLYPH: Record<AssuranceSupportValue, string> = {
  yes: "✓",
  no: "✕",
  qualified: "⦸",
  unknown: "?",
};

/**
 * One cell of a registrar assurance profile (`registrarAssuranceProfile` in
 * `@excellent/core/verification-registrar`): whether a property holds for a
 * given holder class. Four answers, not two or three — collapsing `qualified`
 * or `unknown` into `yes` is the exact misreading this module exists to
 * prevent, so each gets its own tone AND glyph, matching `Verdict` above.
 */
export function AssuranceSupport({ support }: { support: AssuranceSupportValue }) {
  return (
    <span className="vp-badge" data-tone={ASSURANCE_SUPPORT_TONE[support]} data-slot="assurance-support">
      <span className="vp-badge-glyph" aria-hidden="true">
        {ASSURANCE_SUPPORT_GLYPH[support]}
      </span>
      {displayLabel(support)}
    </span>
  );
}

export function DemoScopeBadge({ label }: { label: string }) {
  return (
    <span className="vp-badge" data-tone="accent" data-demo-scoped="true">
      <span className="vp-badge-glyph" aria-hidden="true">
        D
      </span>
      {label}
    </span>
  );
}

export function ToneBadge({
  tone,
  children,
  size,
}: {
  tone: Tone;
  children: ReactNode;
  size?: "lg";
}) {
  return (
    <span className="vp-badge" data-tone={tone} data-size={size}>
      {typeof children === "string" ? displayLabel(children) : children}
    </span>
  );
}

const LIST_PAGE_SIZE = 10;

export function DataTable<R>(props: DataTableProps<R>) {
  return (
    <UiDataTable
      pageSize={LIST_PAGE_SIZE}
      alwaysShowPager
      {...props}
      tableClassName={["vp-responsive-table", props.tableClassName].filter(Boolean).join(" ")}
      columns={props.columns.map((col) => ({
        ...col,
        render: (row: R, index: number) => {
          const node = col.render(row, index);
          return typeof node === "string" ? displayLabel(node) : node;
        },
      }))}
    />
  );
}

export function ListSearch(props: ComponentProps<typeof SearchInput>) {
  const { className, width = "auto", placeholder = "Search…", ...rest } = props;
  return (
    // search-ignore: in-card list filter; dock owns ?q= (Issue 19)
    <SearchInput
      width={width}
      placeholder={placeholder}
      className={["w-full min-w-[12rem]", className].filter(Boolean).join(" ")}
      {...rest}
    />
  );
}

export function Callout({
  tone = "neutral",
  title,
  children,
  actionId,
  ...rest
}: {
  tone?: "accent" | "fail" | "unknown" | "pass" | "neutral" | "waived";
  title?: ReactNode;
  children?: ReactNode;
  actionId?: string;
} & Omit<ComponentProps<"div">, "title" | "children">) {
  return (
    <Alert
      variant={tone === "fail" ? "destructive" : "default"}
      data-tone={tone}
      data-action-id={actionId}
      data-slot="verification-callout"
      {...rest}
    >
      {title ? <AlertTitle>{title}</AlertTitle> : null}
      {children ? (
        <AlertDescription>
          <div>{children}</div>
        </AlertDescription>
      ) : null}
    </Alert>
  );
}

export function Stat({
  label,
  value,
  tone,
  foot,
  onClick,
  actionId,
}: {
  label: string;
  value: ReactNode;
  tone?: "pass" | "fail" | "unknown" | "accent";
  foot?: ReactNode;
  onClick?: () => void;
  actionId?: string;
}) {
  const toneClass =
    tone === "pass"
      ? "text-success"
      : tone === "fail"
        ? "text-destructive"
        : tone === "unknown"
          ? "text-warning"
          : tone === "accent"
            ? "text-primary"
            : "text-foreground";
  const body = (
    <CardContent className="flex flex-col gap-1">
      <span className="type-subtext font-medium text-muted-foreground">{label}</span>
      <span className={`break-words type-heading font-medium tabular-nums ${toneClass}`}>
        {value}
      </span>
      {foot ? <span className="type-subtext text-muted-foreground">{foot}</span> : null}
    </CardContent>
  );
  if (onClick) {
    return (
      <Card interactive asChild>
        <button type="button" onClick={onClick} data-action-id={actionId} className="w-full text-left">
          {body}
        </button>
      </Card>
    );
  }
  return <Card>{body}</Card>;
}

export function StatGrid({ cols, children }: { cols: 2 | 3 | 4; children: ReactNode }) {
  return <div className="vp-stat-grid grid gap-3.5" data-cols={cols}>{children}</div>;
}

export function Section({
  title,
  note,
  actions,
  children,
}: {
  title?: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2.5">
      {title || note || actions ? (
        <SectionTitle variant="plain" size="sm" title={title} subtitle={note} action={actions} />
      ) : null}
      {children}
    </section>
  );
}

export function Fields({ children }: { children: ReactNode }) {
  return <dl className="divide-y divide-border">{children}</dl>;
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 py-2.5 first:pt-0 last:pb-0 sm:grid-cols-[8.5rem_minmax(0,1fr)] sm:gap-3 sm:py-2">
      <dt className="type-body font-medium text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words type-body text-foreground">{children}</dd>
    </div>
  );
}

export function Mono({ children }: { children: ReactNode }) {
  return <span className="tabular-nums">{children}</span>;
}

export function ListRow({
  leading,
  title,
  subtitle,
  aside,
  checkId,
}: {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  aside?: ReactNode;
  checkId?: string;
}) {
  return (
    <div
      className="flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left last:border-b-0"
      data-check-id={checkId}
      data-demo-scoped={aside ? "true" : undefined}
    >
      {leading ? <span className="mt-0.5 shrink-0">{leading}</span> : null}
      <span className="min-w-0 flex-1">
        <span className="block type-body font-medium text-foreground">{title}</span>
        {subtitle ? (
          <span className="mt-0.5 block type-subtext text-muted-foreground">{subtitle}</span>
        ) : null}
      </span>
      {aside ? <span className="shrink-0">{aside}</span> : null}
    </div>
  );
}

export function FilterToolbar({
  children,
  count,
}: {
  children: ReactNode;
  count?: ReactNode;
}) {
  return (
    <div className="flex w-full min-w-0 flex-wrap items-end gap-2">
      {children}
      {count != null ? (
        <>
          <span className="min-w-2 flex-1" />
          <span className="pb-1 type-subtext tabular-nums text-muted-foreground">{count}</span>
        </>
      ) : null}
    </div>
  );
}

export function FilterField({
  label,
  children,
  wide,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <label className={wide ? "flex min-w-[16rem] flex-1 flex-col gap-1" : "flex min-w-[10rem] flex-col gap-1"}>
      <span className="type-subtext leading-none text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export type FindingRecord = {
  id: string;
  title: string;
  ref: string;
  verificationStatus: string;
  severity: string;
  status?: string;
  openedAt: string;
  ask?: string;
  summary: string;
};

export function FindingMeta({
  finding,
  showStatus,
}: {
  finding: FindingRecord;
  showStatus?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 type-subtext text-muted-foreground">
      <Mono>{finding.ref}</Mono>
      <Verdict status={finding.verificationStatus} />
      <Severity level={finding.severity} />
      {showStatus && finding.status ? <span>{displayLabel(finding.status)}</span> : null}
      <span>opened {formatRelativeTime(finding.openedAt)}</span>
    </div>
  );
}

export function FindingCard({
  finding,
  children,
  hideTitle,
}: {
  finding: FindingRecord;
  children?: ReactNode;
  hideTitle?: boolean;
}) {
  return (
    <Card data-queue-item={finding.id}>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          {hideTitle ? null : <h3 className="type-title font-medium text-foreground">{finding.title}</h3>}
          <FindingMeta finding={finding} />
        </div>
        {finding.ask ? (
          <p className="type-body text-foreground">
            <strong>You are being asked to:</strong> {finding.ask}
          </p>
        ) : null}
        <p className="type-body text-muted-foreground">{finding.summary}</p>
        {children ? <div className="flex flex-wrap items-center gap-2">{children}</div> : null}
      </CardContent>
    </Card>
  );
}

export function Stack({ children, gap = "md" }: { children: ReactNode; gap?: "sm" | "md" }) {
  return <div className={gap === "sm" ? "flex flex-col gap-2" : "flex flex-col gap-3.5"}>{children}</div>;
}
